/**
 * @author jakubmaslon
 * @since 2020-08-11 ☀️
 */

import * as React from "react";
import styled from "styled-components";
import { NavLink } from "react-router-dom";

import { rem } from "@ui/helpers";
import theme, { breakpointsValues } from "@ui/themes/default";
import { Heading, Paragraph, Button } from "@ui/Atoms";
import { Variant } from "@ui/Atoms/Button";

interface Props {
  // header
  header?: string;
  isHeaderCentered?: boolean;

  // paragraph
  paragraph?: string;

  // action button
  buttonColor?: Variant;
  buttonText?: string;
  route?: string;
  link?: string;
  onMainButtonClick?: () => void;

  // extra button
  extraButtonText?: string;
  onExtraButtonClick?: () => void;

  // custom content
  children?: React.ReactNode;
}

const MobileScreen = React.memo((props: Props) => (
  <MobileBackground>
    <MobileContainer>
      {props.header && (
        <HeadingStyled type="h6" isHeaderCentered={props.isHeaderCentered}>
          {props.header}
        </HeadingStyled>
      )}

      {props.paragraph && <Paragraph type="body">{props.paragraph}</Paragraph>}

      {props.children && props.children}

      {props.link && (
        <ButtonWrapper>
          <a href={props.link}>
            <Button variant={props.buttonColor} size="large">
              {props.buttonText}
            </Button>
          </a>
        </ButtonWrapper>
      )}

      {props.route && (
        <ButtonWrapper>
          <NavLink to={props.route}>
            <Button variant={props.buttonColor} size="large">
              {props.buttonText}
            </Button>
          </NavLink>
        </ButtonWrapper>
      )}

      {props.onMainButtonClick && (
        <ButtonWrapper>
          <Button
            variant={props.buttonColor}
            size="large"
            onClick={props.onMainButtonClick}
          >
            {props.buttonText}
          </Button>
        </ButtonWrapper>
      )}

      {props.onExtraButtonClick && (
        <Button variant={"textBlue"} onClick={props.onExtraButtonClick}>
          {props.extraButtonText}
        </Button>
      )}
    </MobileContainer>
  </MobileBackground>
));

MobileScreen.displayName = "MobileScreen";

export { MobileScreen };

const MobileBackground = styled.div`
  height: 100vh;
  width: 100vw;
  background: ${theme.palette.white};
`;

const MobileContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100vh;
  max-width: ${rem(breakpointsValues.tablet)};
  margin: 0 auto;
  padding: ${theme.margins.base_x3};
  overflow: scroll;
  background: ${theme.palette.white};
`;

const HeadingStyled = styled(Heading)<{ isHeaderCentered?: boolean }>`
  margin: ${theme.margins.base_x7} 0 ${theme.margins.base_x5};
  font-weight: ${theme.fonts.weights.semibold};
  text-align: ${({ isHeaderCentered }) =>
    isHeaderCentered ? "center" : "left"};
`;

const ButtonWrapper = styled.div`
  max-width: 100%;
  margin: auto auto 0 auto;
`;
